// SummaryReport — the Reports tab's default view. A thin composer: it fetches
// the server-composed summary model and lays the section cards out in the
// order the design spec fixes. It holds no derivation of its own.
//
// Failure has two levels and they are deliberately different. If the summary
// READ fails there is no model at all, so the whole view says so. If the model
// arrives with a section marked unavailable, only that section says so and
// everything else renders — the composer is fail-open per section, which is
// what makes a report of nine independent domains useful when one of them is
// having a bad day.

import { Alert, LoadingSpinner } from "../../common";
import SummaryBand from "./SummaryBand";
import CommentaryCard from "./CommentaryCard";
import EssentialsCard from "./EssentialsCard";
import FinancialPositionCard from "./FinancialPositionCard";
import MilestonesCard from "./MilestonesCard";
import DeliverablesCard from "./DeliverablesCard";
import KpisCard from "./KpisCard";
import IssuesCard from "./IssuesCard";
import VariationsCard from "./VariationsCard";
import InsuranceCard from "./InsuranceCard";
import { useReportSummary } from "../../../hooks/useContractReport";

export interface SummaryReportProps {
  contractId: string;
}

export default function SummaryReport({ contractId }: SummaryReportProps) {
  const { data, isPending, error } = useReportSummary(contractId);

  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="error">
        The summary report couldn&apos;t be loaded. Refresh to try again.
      </Alert>
    );
  }

  const { sections } = data;

  return (
    <div className="space-y-6">
      <SummaryBand section={sections.band} />
      <CommentaryCard contractId={contractId} commentary={data.commentary} />
      <EssentialsCard section={sections.essentials} />
      <FinancialPositionCard section={sections.financial} />
      <MilestonesCard section={sections.milestones} />
      <DeliverablesCard section={sections.deliverables} />
      <KpisCard section={sections.kpis} />
      <IssuesCard section={sections.issues} />
      <VariationsCard section={sections.variations} />
      <InsuranceCard section={sections.insurance} />
    </div>
  );
}
